import React, { useMemo } from 'react';
import _ from 'lodash';
import {
  Text,
  View,
  Page,
  Document,
  StyleSheet,
  Svg,
  Path,
  Font,
  Image
} from '@react-pdf/renderer';

import InvoiceItemsTable from 'components/bill-pdf/invoice-table';

import { BookingLine, TransactionPayment } from 'services/api/type/booking.type';
import { MyInfoType } from 'services/api/type/user.type';
import { Payment } from 'services/api/type/payment.type';
import { PAYMENT_FOR, PAYMENT_STATUS } from 'constants/form';
import { toLocalTime } from 'utils';
import { currencyFormatter } from 'utils/currency';
import { CHARGE_TYPE } from 'components/modal/transaction-modal/print-invoice-v2';
import { BranchesType } from 'services/api/type/branch.type';
import { useTranslation } from 'react-i18next';
import { usePaymentMethodOptions } from 'hooks/usePaymentMethodOptions';

// Register font
Font.register({
  family: 'Inter',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyeMZhrib2Bg-4.ttf',
      fontWeight: 100
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyfMZhrib2Bg-4.ttf',
      fontWeight: 200
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuOKfMZhrib2Bg-4.ttf',
      fontWeight: 300
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf',
      fontWeight: 400
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuI6fMZhrib2Bg-4.ttf',
      fontWeight: 500
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYMZhrib2Bg-4.ttf',
      fontWeight: 600
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf',
      fontWeight: 700
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyYMZhrib2Bg-4.ttf',
      fontWeight: 800
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuBWYMZhrib2Bg-4.ttf',
      fontWeight: 900
    }
  ]
});

const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    paddingTop: 20,
    paddingLeft: 20,
    paddingRight: 20,
    lineHeight: 1.5,
    flexDirection: 'column'
  },

  spaceBetween: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#000'
  },
  flex: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px'
  },

  image: {
    width: 60,
    height: 50,
    marginTop: 10,
    marginLeft: 40
  },

  titleContainer: { flexDirection: 'row', marginTop: 8, marginBottom: 16 },
  billContainer: { flexDirection: 'column', paddingBottom: '12px' },
  billRow: {
    flexDirection: 'row',
    columnGap: '108px'
  },
  billCol: {
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'flex-start'
  },
  billColItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    columnGap: '12px'
  },
  billRowItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    columnGap: '8px',
    marginTop: '8px'
  },

  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#000'
  },

  signatureContainer: {
    flexDirection: 'row',
    marginTop: 28,
    justifyContent: 'space-evenly'
  },
  signatureItem: {
    flexDirection: 'column',
    rowGap: '8px'
  },

  logo: { width: 90 },
  text: { fontSize: 11, fontFamily: 'Inter' },
  textExSmallBold: { fontSize: 11, fontWeight: 600, fontFamily: 'Inter' },
  textBold: { fontSize: 14, fontWeight: 600, fontFamily: 'Inter' },
  textSmallBold: { fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }
});

interface Props {
  bookingLineDetail: BookingLine | undefined;
  payments: Payment[] | undefined;
  me: MyInfoType | undefined;
  dataChange: any;
  signatureStaff: string;
  signatureGuest: string;
  currentBranch: BranchesType | undefined;
  transactionPayment: TransactionPayment[] | undefined;
}

const SERVICE_FEE = 0.05;
// const TAX = 0.08;

export default function NikBillPDF({
  bookingLineDetail,
  payments,
  dataChange,
  signatureStaff,
  signatureGuest,
  currentBranch,
  transactionPayment
}: Props) {
  const { t } = useTranslation();
  const { paymentMethodNames } = usePaymentMethodOptions(t);
  const Logo = () => (
    <Svg width="110" height="90" viewBox="0 0 1255 836" preserveAspectRatio="xMidYMid meet">
      <Path
        d="M 240.50 324.50 L 239.50 506.50 L 141.00 507.02 L 141.00 405.73 C141.00,301.87 141.23,294.87 145.06,279.50 C147.82,268.42 157.51,249.28 165.40,239.33 C172.76,230.04 184.47,219.64 193.96,213.95 C197.51,211.82 242.96,189.37 294.96,164.06 C397.53,114.13 397.84,114.00 414.09,114.00 C434.12,114.00 450.93,120.81 464.43,134.38 C477.24,147.27 482.97,159.15 485.01,177.06 C485.74,183.51 485.99,237.16 485.79,346.50 L 485.50 506.50 L 436.27 506.76 L 387.03 507.02 L 386.77 391.76 L 386.50 276.50 L 383.70 270.50 C375.74,253.46 358.44,246.07 340.37,252.02 C336.59,253.26 317.84,262.11 298.71,271.69 C266.45,287.83 263.45,289.56 257.62,295.30 C250.67,302.15 246.96,307.92 243.08,317.88 ZM 1113.37 506.30 C1112.98,506.69 1085.99,507.00 1053.39,507.00 L 994.10 507.00 L 928.80 437.23 C892.89,398.86 860.24,363.94 856.25,359.63 C843.09,345.41 837.59,330.79 837.57,310.00 C837.56,295.82 838.94,289.79 844.71,278.75 C848.34,271.82 851.68,267.85 942.49,162.65 L 984.49 114.00 L 1043.87 114.00 C1099.87,114.00 1103.17,114.10 1101.87,115.72 C1100.57,117.34 1026.99,195.20 991.03,233.00 C945.54,280.82 938.46,288.65 935.17,294.82 C929.16,306.11 929.81,314.70 937.60,326.73 C939.99,330.42 972.52,363.75 1027.70,419.05 C1075.20,466.65 1113.75,505.92 1113.37,506.30 ZM 817.75 316.14 L 817.50 506.50 L 768.75 506.76 L 720.00 507.02 L 720.00 152.11 L 758.65 133.06 C791.35,116.93 797.98,114.00 801.76,114.00 C807.57,114.00 813.17,117.18 815.88,122.01 L 818.01 125.78 ZM 649.75 328.00 L 649.50 506.50 L 600.75 506.76 L 552.00 507.02 L 552.02 343.26 L 552.03 179.50 L 590.00 159.25 C626.77,139.63 628.14,139.00 633.73,139.00 C640.49,139.01 644.98,141.29 648.01,146.24 C649.97,149.47 649.99,151.37 649.75,328.00 ZM 439.00 722.53 C427.39,724.11 391.48,724.32 379.05,722.88 C348.36,719.32 330.23,704.82 324.03,678.89 C319.23,658.79 319.22,618.27 324.01,599.38 C328.43,581.96 336.77,571.17 351.37,563.97 C365.47,557.02 385.64,554.38 417.53,555.31 C448.59,556.22 465.10,560.81 477.17,571.89 C491.59,585.13 496.87,606.46 495.70,646.88 C494.80,678.15 490.22,693.73 478.47,705.48 C468.71,715.25 457.75,719.98 439.00,722.53 ZM 183.76 687.25 L 183.50 721.50 L 141.00 722.04 L 141.00 557.00 L 184.00 557.00 L 184.00 620.03 L 255.50 619.50 L 256.03 557.00 L 299.00 557.00 L 299.00 722.00 L 279.08 722.00 C268.13,722.00 258.45,721.73 257.58,721.39 C256.18,720.86 256.00,716.88 256.00,686.89 L 256.00 653.00 L 184.03 653.00 ZM 818.05 690.00 L 817.50 721.50 L 756.38 721.76 L 695.26 722.02 L 689.89 719.21 C685.36,716.85 684.04,715.47 681.51,710.52 L 678.50 704.64 L 678.50 573.50 L 681.00 568.50 C683.67,563.16 687.98,559.66 693.88,558.02 C696.12,557.39 720.32,557.01 757.28,557.01 L 817.05 557.00 L 816.50 588.50 L 770.78 589.00 C728.65,589.46 724.93,589.64 723.28,591.28 C721.76,592.80 721.45,595.28 721.18,608.03 L 720.86 623.00 L 815.05 623.00 L 814.78 638.25 L 814.50 653.50 L 721.00 654.02 L 721.00 684.15 L 723.92 687.08 L 726.85 690.00 ZM 604.00 590.00 L 604.00 722.00 L 583.17 722.00 C571.71,722.00 562.03,721.70 561.67,721.33 C561.30,720.97 561.00,691.27 561.00,655.33 L 561.00 590.00 L 500.00 590.00 L 500.00 557.00 L 665.00 557.00 L 665.00 590.00 ZM 978.05 689.00 L 977.50 721.50 L 917.00 721.73 C859.89,721.95 856.28,721.86 852.50,720.11 C850.30,719.09 847.29,716.96 845.80,715.38 C844.94,714.45 844.20,713.72 843.57,712.91 C839.99,708.27 839.99,700.86 840.00,638.73 C840.00,636.25 840.00,633.69 840.00,631.03 L 840.00 557.00 L 881.96 557.00 L 882.23 620.59 L 882.50 684.17 L 885.31 686.59 L 888.11 689.00 ZM 390.76 689.50 C398.67,691.50 422.53,690.92 429.50,688.55 C444.74,683.37 451.11,671.75 452.58,646.50 C453.78,625.69 450.14,607.03 443.32,599.06 C436.40,590.97 421.98,587.17 402.57,588.31 C372.82,590.07 364.12,601.51 364.04,639.00 C364.00,660.44 366.86,672.52 373.80,680.20 C377.99,684.84 382.85,687.50 390.76,689.50 ZM 1113.00 721.00 C1110.46,723.54 1105.98,723.56 1102.78,721.04 C1101.40,719.96 1095.45,711.41 1089.55,702.04 C1083.65,692.67 1078.41,685.00 1077.91,685.00 C1077.40,685.00 1077.00,693.01 1077.00,703.50 L 1077.00 722.00 L 1068.00 722.00 L 1068.00 698.56 C1068.00,675.46 1068.03,675.09 1070.22,673.56 C1073.01,671.60 1077.92,671.55 1080.63,673.45 C1081.77,674.25 1087.60,682.58 1093.59,691.95 C1099.57,701.33 1104.82,709.00 1105.24,709.00 C1105.66,709.00 1106.00,700.90 1106.00,691.00 L 1106.00 673.00 L 1115.00 673.00 L 1115.00 696.00 C1115.00,717.67 1114.88,719.12 1113.00,721.00 ZM 1023.79 635.70 C1020.99,638.39 1020.42,638.51 1009.16,638.83 C1008.17,638.86 1007.24,638.89 1006.37,638.92 C1001.29,639.10 998.28,639.21 996.51,637.78 C993.95,635.72 993.96,630.46 993.99,617.57 C994.00,616.05 994.00,614.42 994.00,612.67 L 994.00 590.00 L 1001.90 590.00 L 1002.20 595.75 L 1002.50 601.50 L 1012.17 602.00 C1020.48,602.43 1022.19,602.82 1024.28,604.79 C1030.90,611.02 1030.61,629.16 1023.79,635.70 ZM 1064.10 627.24 L 1053.21 651.50 L 1049.82 651.80 C1047.95,651.96 1046.15,651.81 1045.80,651.47 C1045.46,651.13 1046.26,648.39 1047.59,645.39 C1048.92,642.40 1050.00,639.34 1050.00,638.61 C1050.00,637.88 1046.44,629.34 1042.09,619.64 L 1034.18 602.00 L 1042.73 602.00 L 1048.19 615.07 C1051.20,622.26 1054.05,628.15 1054.53,628.16 C1055.01,628.17 1057.95,622.40 1061.07,615.34 L 1066.73 602.50 L 1070.87 602.19 C1073.14,602.02 1075.00,602.13 1075.00,602.43 C1075.00,602.73 1070.10,613.89 1064.10,627.24 ZM 1039.38 721.19 C1039.11,721.63 1036.77,721.99 1034.19,721.98 C1029.67,721.96 1029.13,721.62 1019.00,712.36 C1010.34,704.45 1006.21,700.70 1006.19,696.94 C1006.18,693.53 1009.55,690.10 1016.00,683.54 L 1016.19 683.36 L 1026.38 673.00 L 1038.35 673.00 L 1026.68 684.09 C1020.25,690.18 1015.00,695.79 1015.00,696.55 C1015.00,697.30 1020.60,702.97 1027.44,709.15 C1034.29,715.32 1039.66,720.74 1039.38,721.19 ZM 1002.77 697.25 L 1002.50 721.50 L 994.00 722.11 L 994.00 673.00 L 1003.04 673.00 ZM 1003.20 631.80 C1005.17,633.77 1014.41,633.30 1017.14,631.09 C1019.22,629.40 1019.54,628.18 1019.85,620.69 C1020.16,613.13 1019.97,611.97 1018.10,610.10 C1016.37,608.37 1014.78,608.00 1009.00,608.00 L 1002.00 608.00 L 1002.00 619.30 C1002.00,626.76 1002.41,631.01 1003.20,631.80 ZM 1058.00 685.00 L 1058.00 722.00 L 1050.00 722.00 L 1050.00 685.00 ZM 1057.81 675.75 C1057.52,679.30 1057.30,679.52 1053.75,679.81 L 1050.00 680.12 L 1050.00 672.00 L 1058.12 672.00 Z"
        fill="rgb(26,71,42)"
      />
      <Path
        d="M 0.00 418.00 L 0.00 0.00 L 627.50 0.00 L 1255.00 0.00 L 1255.00 418.00 L 1255.00 836.00 L 627.50 836.00 L 0.00 836.00 L 0.00 418.00 ZM 439.00 722.53 C457.75,719.98 468.71,715.25 478.47,705.48 C490.22,693.73 494.80,678.15 495.70,646.88 C496.87,606.46 491.59,585.13 477.17,571.89 C465.10,560.81 448.59,556.22 417.53,555.31 C385.64,554.38 365.47,557.02 351.37,563.97 C336.77,571.17 328.43,581.96 324.01,599.38 C319.22,618.27 319.23,658.79 324.03,678.89 C330.23,704.82 348.36,719.32 379.05,722.88 C391.48,724.32 427.39,724.11 439.00,722.53 ZM 1113.00 721.00 C1114.88,719.12 1115.00,717.67 1115.00,696.00 L 1115.00 673.00 L 1110.50 673.00 L 1106.00 673.00 L 1106.00 691.00 C1106.00,700.90 1105.66,709.00 1105.24,709.00 C1104.82,709.00 1099.57,701.33 1093.59,691.95 C1087.60,682.58 1081.77,674.25 1080.63,673.45 C1077.92,671.55 1073.01,671.60 1070.22,673.56 C1068.03,675.09 1068.00,675.46 1068.00,698.56 L 1068.00 722.00 L 1072.50 722.00 L 1077.00 722.00 L 1077.00 703.50 C1077.00,693.01 1077.40,685.00 1077.91,685.00 C1078.41,685.00 1083.65,692.67 1089.55,702.04 C1095.45,711.41 1101.40,719.96 1102.78,721.04 C1105.98,723.56 1110.46,723.54 1113.00,721.00 ZM 183.76 687.25 L 184.03 653.00 L 220.01 653.00 L 256.00 653.00 L 256.00 686.89 C256.00,716.88 256.18,720.86 257.58,721.39 C258.45,721.73 268.13,722.00 279.08,722.00 L 299.00 722.00 L 299.00 639.50 L 299.00 557.00 L 277.51 557.00 L 256.03 557.00 L 255.76 588.25 L 255.50 619.50 L 219.75 619.76 L 184.00 620.03 L 184.00 588.51 L 184.00 557.00 L 162.50 557.00 L 141.00 557.00 L 141.00 639.52 L 141.00 722.04 L 162.25 721.77 L 183.50 721.50 L 183.76 687.25 ZM 604.00 656.00 L 604.00 590.00 L 634.50 590.00 L 665.00 590.00 L 665.00 573.50 L 665.00 557.00 L 582.50 557.00 L 500.00 557.00 L 500.00 573.50 L 500.00 590.00 L 530.50 590.00 L 561.00 590.00 L 561.00 655.33 C561.00,691.27 561.30,720.97 561.67,721.33 C562.03,721.70 571.71,722.00 583.17,722.00 L 604.00 722.00 L 604.00 656.00 ZM 817.78 705.75 L 818.05 690.00 L 772.45 690.00 L 726.85 690.00 L 723.92 687.08 L 721.00 684.15 L 721.00 669.09 L 721.00 654.02 L 767.75 653.76 L 814.50 653.50 L 814.78 638.25 L 815.05 623.00 L 767.95 623.00 L 720.86 623.00 L 721.18 608.03 C721.45,595.28 721.76,592.80 723.28,591.28 C724.93,589.64 728.65,589.46 770.78,589.00 L 816.50 588.50 L 816.78 572.75 L 817.05 557.00 L 757.28 557.01 C720.32,557.01 696.12,557.39 693.88,558.02 C687.98,559.66 683.67,563.16 681.00,568.50 L 678.50 573.50 L 678.50 639.07 L 678.50 704.64 L 681.51 710.52 C684.04,715.47 685.36,716.85 689.89,719.21 L 695.26 722.02 L 756.38 721.76 L 817.50 721.50 L 817.78 705.75 ZM 977.77 705.25 L 978.05 689.00 L 933.08 689.00 L 888.11 689.00 L 885.31 686.59 L 882.50 684.17 L 882.23 620.59 L 881.96 557.00 L 860.98 557.00 L 840.00 557.00 L 840.00 631.03 C840.00,711.93 839.80,708.97 845.80,715.38 C847.29,716.96 850.30,719.09 852.50,720.11 C856.28,721.86 859.89,721.95 917.00,721.73 L 977.50 721.50 L 977.77 705.25 ZM 1002.77 697.25 L 1003.04 673.00 L 998.52 673.00 L 994.00 673.00 L 994.00 697.56 L 994.00 722.11 L 998.25 721.81 L 1002.50 721.50 L 1002.77 697.25 ZM 1039.38 721.19 C1039.66,720.74 1034.29,715.32 1027.44,709.15 C1020.60,702.97 1015.00,697.30 1015.00,696.55 C1015.00,695.79 1020.25,690.18 1026.68,684.09 L 1038.35 673.00 L 1032.37 673.00 L 1026.38 673.00 L 1016.19 683.36 C1002.43,697.34 1002.42,697.21 1019.00,712.36 C1029.13,721.62 1029.67,721.96 1034.19,721.98 C1036.77,721.99 1039.11,721.63 1039.38,721.19 ZM 1058.00 703.50 L 1058.00 685.00 L 1054.00 685.00 L 1050.00 685.00 L 1050.00 703.50 L 1050.00 722.00 L 1054.00 722.00 L 1058.00 722.00 L 1058.00 703.50 ZM 1057.81 675.75 L 1058.12 672.00 L 1054.06 672.00 L 1050.00 672.00 L 1050.00 676.06 L 1050.00 680.12 L 1053.75 679.81 C1057.30,679.52 1057.52,679.30 1057.81,675.75 ZM 1064.10 627.24 C1070.10,613.89 1075.00,602.73 1075.00,602.43 C1075.00,602.13 1073.14,602.02 1070.87,602.19 L 1066.73 602.50 L 1061.07 615.34 C1057.95,622.40 1055.01,628.17 1054.53,628.16 C1054.05,628.15 1051.20,622.26 1048.19,615.07 L 1042.73 602.00 L 1038.46 602.00 L 1034.18 602.00 L 1042.09 619.64 C1046.44,629.34 1050.00,637.88 1050.00,638.61 C1050.00,639.34 1048.92,642.40 1047.59,645.39 C1046.26,648.39 1045.46,651.13 1045.80,651.47 C1046.15,651.81 1047.95,651.96 1049.82,651.80 L 1053.21 651.50 L 1064.10 627.24 ZM 1023.79 635.70 C1030.61,629.16 1030.90,611.02 1024.28,604.79 C1022.19,602.82 1020.48,602.43 1012.17,602.00 L 1002.50 601.50 L 1002.20 595.75 L 1001.90 590.00 L 997.95 590.00 L 994.00 590.00 L 994.00 612.67 C994.00,641.10 992.94,639.28 1009.16,638.83 C1020.42,638.51 1020.99,638.39 1023.79,635.70 ZM 240.00 415.50 L 240.50 324.50 L 243.08 317.88 C246.96,307.92 250.67,302.15 257.62,295.30 C263.45,289.56 266.45,287.83 298.71,271.69 C317.84,262.11 336.59,253.26 340.37,252.02 C358.44,246.07 375.74,253.46 383.70,270.50 L 386.50 276.50 L 386.77 391.76 L 387.03 507.02 L 436.27 506.76 L 485.50 506.50 L 485.79 346.50 C485.99,237.16 485.74,183.51 485.01,177.06 C482.97,159.15 477.24,147.27 464.43,134.38 C450.93,120.81 434.12,114.00 414.09,114.00 C397.84,114.00 397.53,114.13 294.96,164.06 C242.96,189.37 197.51,211.82 193.96,213.95 C184.47,219.64 172.76,230.04 165.40,239.33 C157.51,249.28 147.82,268.42 145.06,279.50 C141.23,294.87 141.00,301.87 141.00,405.73 L 141.00 507.02 L 190.25 506.76 L 239.50 506.50 L 240.00 415.50 ZM 649.75 328.00 C649.99,151.37 649.97,149.47 648.01,146.24 C644.98,141.29 640.49,139.01 633.73,139.00 C628.14,139.00 626.77,139.63 590.00,159.25 L 552.03 179.50 L 552.02 343.26 L 552.00 507.02 L 600.75 506.76 L 649.50 506.50 L 649.75 328.00 ZM 817.75 316.14 L 818.01 125.78 L 815.88 122.01 C813.17,117.18 807.57,114.00 801.76,114.00 C797.98,114.00 791.35,116.93 758.65,133.06 L 720.00 152.11 L 720.00 329.57 L 720.00 507.02 L 768.75 506.76 L 817.50 506.50 L 817.75 316.14 ZM 1113.37 506.30 C1113.75,505.92 1075.20,466.65 1027.70,419.05 C972.52,363.75 939.99,330.42 937.60,326.73 C929.81,314.70 929.16,306.11 935.17,294.82 C938.46,288.65 945.54,280.82 991.03,233.00 C1026.99,195.20 1100.57,117.34 1101.87,115.72 C1103.17,114.10 1099.87,114.00 1043.87,114.00 L 984.49 114.00 L 942.49 162.65 C851.68,267.85 848.34,271.82 844.71,278.75 C838.94,289.79 837.56,295.82 837.57,310.00 C837.59,330.79 843.09,345.41 856.25,359.63 C860.24,363.94 892.89,398.86 928.80,437.23 L 994.10 507.00 L 1053.39 507.00 C1085.99,507.00 1112.98,506.69 1113.37,506.30 ZM 390.76 689.50 C382.85,687.50 377.99,684.84 373.80,680.20 C366.86,672.52 364.00,660.44 364.04,639.00 C364.12,601.51 372.82,590.07 402.57,588.31 C421.98,587.17 436.40,590.97 443.32,599.06 C450.14,607.03 453.78,625.69 452.58,646.50 C451.11,671.75 444.74,683.37 429.50,688.55 C422.53,690.92 398.67,691.50 390.76,689.50 ZM 1003.20 631.80 C1002.41,631.01 1002.00,626.76 1002.00,619.30 L 1002.00 608.00 L 1009.00 608.00 C1014.78,608.00 1016.37,608.37 1018.10,610.10 C1019.97,611.97 1020.16,613.13 1019.85,620.69 C1019.54,628.18 1019.22,629.40 1017.14,631.09 C1014.41,633.30 1005.17,633.77 1003.20,631.80 Z"
        fill="rgb(254,254,254)"
      />
    </Svg>
  );

  const HotelInfo = () => (
    <View style={styles.titleContainer}>
      <View style={styles.spaceBetween}>
        <View>
          <Text style={{ ...styles.textBold, textTransform: 'uppercase' }}>
            {currentBranch?.name}
          </Text>
          <Text style={styles.text}>Address: {currentBranch?.address}</Text>
          <Text style={styles.text}>
            {currentBranch?.phone_contact ? `Tel: ${currentBranch?.phone_contact}` : ''}
          </Text>
        </View>
        <Logo />
      </View>
    </View>
  );

  const BillInfo = () => (
    <View style={styles.billContainer}>
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px'
        }}
      >
        <Text style={{ ...styles.textBold, fontSize: 20 }}>HÓA ĐƠN</Text>
        <Text style={{ fontSize: 14 }}>(INVOICE)</Text>
      </View>

      <View style={styles.billRow}>
        <View style={{ ...styles.billCol, width: '40%' }}>
          <View style={styles.billColItem}>
            <Text style={styles.text}>Ngày (Date):</Text>
            <Text style={styles.textExSmallBold}>
              {dataChange?.createdDate?.format('DD/MM/YYYY')}
            </Text>
          </View>
        </View>
        <View style={{ ...styles.billCol, width: '60%' }}>
          <View style={styles.billColItem}>
            <Text style={{ ...styles.text, width: '65%' }}>Số tham chiếu (Folio No.):</Text>
            <Text style={{ ...styles.textExSmallBold, width: '35%' }}>
              {bookingLineDetail?.sale_order_name}
            </Text>
          </View>
          <View style={styles.billColItem}>
            <Text style={{ ...styles.text, width: '65%' }}>Số phòng (Room No.):</Text>
            <Text style={{ ...styles.textExSmallBold, width: '35%' }}>
              {bookingLineDetail?.room_name}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.billRowItem}>
        <Text style={{ ...styles.text }}>Tên khách hàng (Guest Name):</Text>
        <Text style={{ ...styles.textExSmallBold }}>
          {dataChange?.partnerName !== undefined
            ? dataChange?.partnerName
            : bookingLineDetail?.partner_name}
        </Text>
      </View>

      <View style={styles.billRowItem}>
        <Text style={{ ...styles.text }}>Công ty (Company):</Text>
        <Text style={{ ...styles.textExSmallBold }}>{dataChange?.company}</Text>
      </View>

      <View style={styles.billRowItem}>
        <Text style={{ ...styles.text }}>Địa chỉ (Address):</Text>
        <Text style={{ ...styles.textExSmallBold }}>{dataChange?.companyAddress}</Text>
      </View>

      <View style={{ ...styles.billRow, marginTop: '8px' }}>
        <View style={{ ...styles.billCol, width: '47%' }}>
          <View style={styles.billColItem}>
            <Text style={styles.text}>Mã số thuế (Tax Code):</Text>
            <Text style={styles.textExSmallBold}>{dataChange?.taxCode}</Text>
          </View>
        </View>
        <View style={{ ...styles.billCol, width: '53%' }}>
          <View style={styles.billColItem}>
            <Text style={{ ...styles.text, width: '65%' }}>Ngày đến (Arrival):</Text>
            <Text style={{ ...styles.textExSmallBold, width: '35%' }}>
              {bookingLineDetail?.check_in
                ? toLocalTime(bookingLineDetail?.check_in, 'DD/MM/YYYY')
                : ''}
            </Text>
          </View>
          <View style={styles.billColItem}>
            <Text style={{ ...styles.text, width: '65%' }}>Ngày đi (Departure):</Text>
            <Text style={{ ...styles.textExSmallBold, width: '35%' }}>
              {bookingLineDetail?.check_out
                ? toLocalTime(bookingLineDetail?.check_out, 'DD/MM/YYYY')
                : ''}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  const Signature = () => (
    <View style={styles.signatureContainer}>
      <View style={styles.signatureItem}>
        <Text style={styles.textSmallBold}>Khách hàng (Customer):</Text>
        <Text style={styles.text}>Ký và ghi rõ họ tên (Sign and full name)</Text>
        {signatureGuest && <Image style={styles.image} src={signatureGuest} />}
      </View>
      <View style={styles.signatureItem}>
        <Text style={styles.textSmallBold}>Thu ngân (Cashier):</Text>
        <Text style={styles.text}>Ký và ghi rõ họ tên (Sign and full name)</Text>
        {signatureStaff && <Image style={styles.image} src={signatureStaff} />}
      </View>
    </View>
  );

  const totalPaidAmount = useMemo(() => {
    return (
      transactionPayment?.reduce((cur, next) => {
        return cur + next.amount;
      }, 0) || 0
    );
  }, [transactionPayment]);

  const paymentItems = useMemo(() => {
    const { chargeType } = dataChange;

    const taxBooking = (bookingLineDetail?.tax || 0) / 100;

    let bookingPrices =
      bookingLineDetail?.booking_prices.map(item => {
        const subAmount = Math.ceil(item.final_price / (1 + SERVICE_FEE + taxBooking));
        const serviceFee = Math.ceil(subAmount * SERVICE_FEE);
        const tax = Math.ceil(subAmount * taxBooking);
        return {
          date: toLocalTime(item.start_date, 'DD/MM/YYYY'),
          detail: `Tiền phòng (Room charge)`,
          price: subAmount.toLocaleString('vn'),
          serviceFee: serviceFee.toLocaleString('vn'),
          [`tax-${bookingLineDetail.tax}`]: tax.toLocaleString('vn'),
          amount: item.final_price.toLocaleString('vn')
        };
      }) || [];
    if (chargeType === CHARGE_TYPE.SERVICE) {
      bookingPrices = [];
    }

    let services: any[] = [];
    if (chargeType === CHARGE_TYPE.ALL || chargeType === CHARGE_TYPE.SERVICE) {
      services =
        bookingLineDetail?.services
          ?.filter(item => !item.is_deleted)
          .map(item => {
            const taxService = (item.tax || 0) / 100;
            const subAmount = Math.ceil(item.total_price / (1 + SERVICE_FEE + taxService));
            const serviceFee = Math.ceil(subAmount * SERVICE_FEE);
            const tax = Math.ceil(subAmount * taxService);
            return {
              date: toLocalTime(item.created_date, 'DD/MM/YYYY'),
              detail: item.name,
              price: subAmount.toLocaleString('vn'),
              qty: item.qty,
              serviceFee: serviceFee.toLocaleString('vn'),
              [`tax-${item.tax}`]: tax.toLocaleString('vn'),
              amount: item.total_price.toLocaleString('vn')
            };
          }) || [];
    }

    const filteredPayments =
      payments
        ?.filter(item => {
          let andCondition = true;
          if (chargeType === CHARGE_TYPE.ROOM) {
            andCondition = item.income_type === PAYMENT_FOR.ROOM;
          } else if (chargeType === CHARGE_TYPE.SERVICE) {
            andCondition = item.income_type === PAYMENT_FOR.SERVICE;
          }
          return item.state !== PAYMENT_STATUS.CANCELLED && andCondition;
        })
        .map(payment => ({
          date: toLocalTime(payment.payment_date, 'DD/MM/YYYY'),
          detail: `Thanh toán ${paymentMethodNames[payment.payment_method]}`,
          price: '',
          qty: '',
          serviceFee: '',
          tax: '',
          amount: `-${payment.amount?.toLocaleString('vn')}`
        })) || [];

    const result = [...bookingPrices, ...services, ...filteredPayments];

    return result;
    // eslint-disable-next-line
  }, [bookingLineDetail, payments, dataChange]);

  const totalPayment = useMemo(() => {
    const { chargeType } = dataChange;
    const totalPayment = payments
      ?.filter(item => {
        let andCondition = true;
        if (chargeType === CHARGE_TYPE.ROOM) {
          andCondition = item.income_type === PAYMENT_FOR.ROOM;
        } else if (chargeType === CHARGE_TYPE.SERVICE) {
          andCondition = item.income_type === PAYMENT_FOR.SERVICE;
        }
        return item.state !== PAYMENT_STATUS.CANCELLED && andCondition;
      })
      .reduce((cur, next) => {
        return cur + next.amount;
      }, 0);

    return totalPayment;
  }, [dataChange, payments]);

  const totalPrice = useMemo(() => {
    const { chargeType } = dataChange;
    let total = 0;
    const totalBookingPrice =
      bookingLineDetail?.booking_prices.reduce((cur, next) => {
        return cur + next.final_price;
      }, 0) || 0;

    const totalServicePrice =
      bookingLineDetail?.services?.reduce((cur, next) => {
        return cur + next.total_price;
      }, 0) || 0;

    switch (chargeType) {
      case CHARGE_TYPE.ALL:
        total = bookingLineDetail?.total_price || 0;
        break;
      case CHARGE_TYPE.ROOM:
        total = totalBookingPrice;
        break;
      case CHARGE_TYPE.SERVICE:
        total = totalServicePrice;
        break;
      default:
        break;
    }
    return total;
  }, [
    bookingLineDetail?.booking_prices,
    bookingLineDetail?.services,
    bookingLineDetail?.total_price,
    dataChange
  ]);

  const listTax = useMemo(() => {
    const result = {};
    const { chargeType } = dataChange;

    if (!_.isEmpty(bookingLineDetail)) {
      if (
        _.isEmpty(_.get(result, bookingLineDetail?.tax, '')) &&
        [CHARGE_TYPE.ALL, CHARGE_TYPE.ROOM].includes(chargeType)
      ) {
        _.set(result, bookingLineDetail?.tax, true);
      }

      if ([CHARGE_TYPE.ALL, CHARGE_TYPE.SERVICE].includes(chargeType)) {
        bookingLineDetail?.services
          ?.filter(item => !item.is_deleted)
          .forEach(item => {
            if (_.isEmpty(_.get(result, item.tax, ''))) {
              _.set(result, item.tax, true);
            }
          });
      }
    }
    return result;
  }, [bookingLineDetail, dataChange]);

  return (
    <Document title={t('common.bookingData.receipt')} language="vi">
      <Page wrap size="A4" style={styles.page}>
        <HotelInfo />
        <BillInfo />

        <InvoiceItemsTable
          listTax={listTax}
          paymentItems={paymentItems}
          remainAmount={totalPrice - Math.abs(Number(totalPayment) + totalPaidAmount || 0)}
          transactionPayment={transactionPayment}
          paidAmount={totalPaidAmount}
          totalPrice={
            totalPrice.toLocaleString('vn') || bookingLineDetail?.total_price?.toLocaleString('vn')
          }
        />

        <View
          break={paymentItems.length >= 8}
          style={{
            ...styles.totalRow,
            marginTop: '12px',
            width: '80%'
          }}
        >
          <Text style={styles.text}>Tổng cộng tiền thanh toán (Total Amount):</Text>
          <Text style={{ ...styles.text, fontSize: 12 }}>
            {totalPrice.toLocaleString('vn') ||
              bookingLineDetail?.total_price?.toLocaleString('vn')}
          </Text>
        </View>

        <View style={{ ...styles.flex, marginTop: '6px' }}>
          <Text style={styles.text}>Số tiền bằng chữ (Amount in words):</Text>
          <Text style={styles.text}>
            {currencyFormatter(totalPrice) ||
              (bookingLineDetail?.total_price
                ? currencyFormatter(bookingLineDetail?.total_price)
                : '')}
          </Text>
        </View>

        <Signature />
      </Page>
    </Document>
  );
}
